/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/
import { ICommerceApiSettings, ICoreContext, IGeneric, IAny } from '@msdyn365-commerce/core-internal';
import { EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import * as React from 'react';
import { Modal, ModalBody, ModalFooter } from '@msdyn365-commerce-modules/utilities';
import { IBuyboxCommonResources } from '../buyboxInterface';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { _readFromStorage, _removeFromStorage } from '../../../../utils/helper';
import { ISwatchItem } from '../swatch/swatch-item';

/**
 * Swatch item configuration.
 */
export interface ISwatchItemProps {
    className: string;
    apiSettings: ICommerceApiSettings;
    requestContext: ICoreContext<IGeneric<IAny>>;
    resources: IBuyboxCommonResources;

    isSelected: boolean;
    isColored: boolean;
    isSelectionEnabled?: boolean;

    item: ISwatchItem;

    onSelectItem(item: ISwatchItem): void;

    triggerOOSEmailHandler(email: string): Promise<boolean>;

    swatchProducts: SimpleProduct[];

    triggerOOSModal: () => string;
}

/**
 * Represents a single item for swatch.
 * @param props - Item configuration.
 * @returns React node.
 */
const OOSLSKey = 'triggerOOSModal';
export const EmailComponent: React.FC<ISwatchItemProps> = (props: ISwatchItemProps) => {
    const trrigerModal = !!_readFromStorage(OOSLSKey);
    const {
        isSelected,
        isColored,
        item,
        requestContext: {
            request: { user },
            telemetry
        }
    } = props;

    const { value } = item;
    const oosContainer = 'msc-outofstock-modal';
    const [agree, setAgree] = React.useState<boolean>(false);
    const [mailSent, setMailSent] = React.useState<boolean>(false);
    const [showOOSEmailPopup, setShowOOSEmailPopup] = React.useState<boolean>(false);
    const [emailAddress, setEmailAddress] = React.useState<string>('');
    const [loader, setLoader] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (trrigerModal) {
            if (user?.isAuthenticated) {
                props.triggerOOSEmailHandler(user?.emailAddress!).catch(error => {
                    telemetry.error(error.message);
                    telemetry.debug('Unable to send OOS notification');
                });
            } else {
                setShowOOSEmailPopup(trrigerModal || false);
            }
        }
        _removeFromStorage(OOSLSKey);
    }, [trrigerModal]);

    const selectedSize = isSelected && !isColored && value;

    const _changeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailAddress(event.target.value);
    };

    const _checkboxHandler = () => {
        setAgree(!agree);
    };

    const _submitHandler = () => {
        setLoader(true);
        if (_isEmailValid() && props.triggerOOSEmailHandler(emailAddress)) {
            setLoader(false);
            setShowOOSEmailPopup(false);
        } else {
            setLoader(false);
        }
    };

    const _isEmailValid = (): boolean => {
        return EmailRegex.defaultRegex.test(emailAddress);
    };

    const _oosIconHandler = async (): Promise<void> => {
        const user = props?.requestContext.request.user;
        if (user?.isAuthenticated) {
            await props.triggerOOSEmailHandler(user?.emailAddress!);
        } else {
            setShowOOSEmailPopup(true);
            const resp = props.triggerOOSModal().split('|');
            if (resp[0] === 'true') {
                setMailSent(true);
            } else {
                setEmailAddress(resp[1]);
                setMailSent(false);
            }
        }
    };

    return (
        <>
            <div className='outofstock-mail' onClick={_oosIconHandler}></div>
            <Modal isOpen={showOOSEmailPopup} className={`${oosContainer}`} toggle={() => setShowOOSEmailPopup(!showOOSEmailPopup)}>
                <ModalBody className={`${oosContainer}__dialog__body`}>
                    <button id='cross-modal' onClick={() => setShowOOSEmailPopup(false)}>
                        X
                    </button>
                    {mailSent ? (
                        <div className='email-Success-wrapper'>
                            <h1>{props.resources.emailSuccessHeading}</h1>
                            <h2>{props.resources.emailSuccessContent}</h2>
                        </div>
                    ) : (
                        <div className='email-box-wrapper'>
                            <div className='outofstocktitle-box'>
                                <h1>{props.resources.emailOutOfStock}</h1>
                            </div>
                            <div className='outofstock-msg-box'>
                                {props.resources.emailOutOfStockMsg1} <span>{selectedSize}&nbsp;</span>
                                {props.resources.emailOutOfStockMsg3}
                            </div>
                            <div className='email-wrapper-box'>
                                <input
                                    type='email'
                                    className='ms-checkout-gift-card__input-text form-control'
                                    onChange={_changeEmail}
                                    placeholder='E-mail'
                                    value={emailAddress}
                                />
                                <button
                                    disabled={!agree}
                                    className={`${oosContainer}__btn-apply ms-checkout-gift-card__btn-apply msc-btn`}
                                    onClick={_submitHandler}
                                >
                                    {props.resources.submitTxt}
                                    {loader && <span className={`${oosContainer}__spinner spinner-border spinner-border-sm`}></span>}
                                </button>
                            </div>
                            <div className='custom__checkbox notify-box'>
                                <label className='custom__checkbox__label'>
                                    <input
                                        type='checkbox'
                                        id='agree'
                                        value={`${agree}`}
                                        checked={agree}
                                        onChange={_checkboxHandler}
                                        className='custom__checkbox__input'
                                    />
                                    <p>
                                        <div>{props.resources.notifyBoxIAgree}</div>
                                    </p>
                                </label>
                            </div>
                            {!_isEmailValid() && emailAddress.length > 0 && (
                                <div className='error-msg'>{props.resources.emailErrorMsg}</div>
                            )}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className={`${oosContainer}__dialog__footer`} />
            </Modal>
        </>
    );
};
